import { useState, useCallback, useRef, useEffect } from 'react'
import { FileText, Pencil, X, Check } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent } from '@neo-agents/ui'
import type { ChatMessage } from '../state/types'
import { AttachmentImage } from './AttachmentImage'
import { FilePreview, isPreviewableFile } from './FilePreview'

interface UserMessageBubbleProps {
  message: ChatMessage
  onEdit?: (messageId: string, newContent: string) => void
}

export function UserMessageBubble({ message, onEdit }: UserMessageBubbleProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(message.content)
  const [filePreview, setFilePreview] = useState<{ src: string; name: string; mimeType: string } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const imageAttachments = message.attachments?.filter((a) => a.type.startsWith('image/')) ?? []
  const fileAttachments = message.attachments?.filter((a) => !a.type.startsWith('image/')) ?? []

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(editValue.length, editValue.length)
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [isEditing, editValue.length])

  const handleStartEdit = useCallback(() => {
    setEditValue(message.content)
    setIsEditing(true)
  }, [message.content])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setEditValue(message.content)
  }, [message.content])

  const handleSaveEdit = useCallback(() => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== message.content && onEdit) {
      onEdit(message.id, trimmed)
    }
    setIsEditing(false)
  }, [editValue, message.content, message.id, onEdit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSaveEdit()
      } else if (e.key === 'Escape') {
        handleCancelEdit()
      }
    },
    [handleSaveEdit, handleCancelEdit],
  )

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditValue(e.target.value)
    e.target.style.height = 'auto'
    e.target.style.height = `${e.target.scrollHeight}px`
  }, [])

  const openFilePreview = useCallback((src: string, name: string, mimeType: string) => {
    setFilePreview({ src, name, mimeType })
  }, [])

  const closeFilePreview = useCallback(() => {
    setFilePreview(null)
  }, [])

  return (
    <div className="flex justify-end py-4 group">
      <div className="max-w-[80%]">
        {/* Image previews */}
        {imageAttachments.length > 0 && (
          <div className="mb-2 flex flex-wrap justify-end gap-2">
            {imageAttachments.map((att) => (
              <div key={att.id} className="shrink-0">
                <AttachmentImage
                  src={att.url}
                  mimeType={att.type}
                  alt={att.name}
                  className="block h-auto max-h-[200px] w-auto max-w-[200px] shrink-0 rounded-base border-2 border-nb-border object-cover"
                />
              </div>
            ))}
          </div>
        )}

        {/* File attachment badges */}
        {fileAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2 justify-end">
            {fileAttachments.map((att) => (
              <button
                key={att.id}
                type="button"
                className="inline-flex items-center gap-1 rounded-base border-2 border-nb-border bg-secondary-bg px-2 py-1 text-xs font-base text-foreground transition-colors hover:bg-main hover:text-main-foreground"
                onClick={() => {
                  if (isPreviewableFile(att.type, att.name)) {
                    openFilePreview(att.url, att.name, att.type)
                  }
                }}
              >
                <FileText className="h-3 w-3" />
                <span className="truncate max-w-[120px]">{att.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Text content */}
        {isEditing ? (
          <div className="rounded-base border-2 border-nb-border bg-main/15 px-4 py-2.5 nb-shadow-sm">
            <textarea
              ref={textareaRef}
              className="w-full resize-none bg-transparent text-base text-foreground outline-none min-h-[24px] max-h-[200px] font-base"
              value={editValue}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <div className="flex justify-end gap-1 mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="rounded-base border-2 border-nb-border p-1 text-foreground hover:bg-destructive/10 transition-colors"
                    onClick={handleCancelEdit}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Cancel</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="rounded-base border-2 border-nb-border p-1 bg-main text-main-foreground hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                    onClick={handleSaveEdit}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Save & regenerate</TooltipContent>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className="relative">
            {message.content && (
              <div className="rounded-base border-2 border-nb-border bg-main/15 px-4 py-2.5 text-base text-foreground whitespace-pre-wrap font-base nb-shadow-sm">
                {message.content}
              </div>
            )}
            {/* Edit button on hover */}
            {onEdit && (
              <div className="absolute -left-9 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-base border-2 border-nb-border p-1 text-foreground/50 hover:bg-main/20 transition-colors"
                      onClick={handleStartEdit}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Edit message</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        )}
      </div>
      {filePreview && (
        <FilePreview
          src={filePreview.src}
          fileName={filePreview.name}
          mimeType={filePreview.mimeType}
          open={true}
          onClose={closeFilePreview}
        />
      )}
    </div>
  )
}
